import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { AuthGuard } from '@/guards/AuthGuard';
import { CareFeatureRouteGuard } from '@/guards/CareFeatureRouteGuard';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { lazyWithRetryRoute } from '@/lib/lazyWithRetry';
import { routeModuleLoaders } from './prefetch';

// Eager-loaded routes
import Login from './login';
import NotFound from './not-found';

// Lazy-loaded routes — shared module loaders keep router and prefetch mapping in sync
const dashboardLazy = lazyWithRetryRoute(routeModuleLoaders.dashboard);
const locationOverviewLazy = lazyWithRetryRoute(routeModuleLoaders.locationOverview);
const handoverLazy = lazyWithRetryRoute(routeModuleLoaders.handover);
const residentsLazy = lazyWithRetryRoute(routeModuleLoaders.residents);
const residentDetailLazy = lazyWithRetryRoute(routeModuleLoaders.residentDetail);
const sisLazy = lazyWithRetryRoute(routeModuleLoaders.sis);
const settingsLazy = lazyWithRetryRoute(routeModuleLoaders.settings);
const woundsLazy = lazyWithRetryRoute(routeModuleLoaders.wounds);
const woundDetailLazy = lazyWithRetryRoute(routeModuleLoaders.woundDetail);
const woundApprovalLazy = lazyWithRetryRoute(routeModuleLoaders.woundApproval);
const medicationsLazy = lazyWithRetryRoute(routeModuleLoaders.medications);
const admissionsLazy = lazyWithRetryRoute(routeModuleLoaders.admissions);
const occupancyLazy = lazyWithRetryRoute(routeModuleLoaders.occupancy);
const inventoryLazy = lazyWithRetryRoute(routeModuleLoaders.inventory);
const scheduleLazy = lazyWithRetryRoute(routeModuleLoaders.schedule);
const inboxLazy = lazyWithRetryRoute(routeModuleLoaders.inbox);
const contactsLazy = lazyWithRetryRoute(routeModuleLoaders.contacts);
const practiceRequestsLazy = lazyWithRetryRoute(routeModuleLoaders.practiceRequests);
const netzwerkLazy = lazyWithRetryRoute(routeModuleLoaders.netzwerk);
const adminGlossaryLazy = lazyWithRetryRoute(routeModuleLoaders.adminGlossary);

// Public — anon, KEIN AuthGuard. ADR-0007 Receiver-Side Wizard.
const admissionPublicLazy = lazyWithRetryRoute(routeModuleLoaders.admissionPublic);
// Public — anon, KEIN AuthGuard. ADR-0008 Self-Service Inquiry.
const admissionInquiryPublicLazy = lazyWithRetryRoute(routeModuleLoaders.admissionInquiryPublic);
// Public — anon, KEIN AuthGuard. Family-Portal Universal-Link Bridge.
const familyPortalReceiverLazy = lazyWithRetryRoute(routeModuleLoaders.familyPortalReceiver);
// Authenticated — PDL Inbox-Tab Anfragen (Phase 4).
const inquiriesLazy = lazyWithRetryRoute(routeModuleLoaders.inquiries);
// Authenticated — PDL Family-Portal Hub pro Bewohner.
const residentFamilyPortalHubLazy = lazyWithRetryRoute(routeModuleLoaders.residentFamilyPortalHub);

export const router = createBrowserRouter([
    {
        path: '/login',
        element: <Login />,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    },
    // Public Admission-Wizard for relatives — kein Auth, kein AppShell.
    {
        path: '/admission/:token',
        lazy: admissionPublicLazy,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    },
    // Public Self-Service-Inquiry-Landing — kein Auth, kein AppShell. ADR-0008 Phase 4.
    {
        path: '/aufnahme/:slug',
        lazy: admissionInquiryPublicLazy,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    },
    // Public 404 fuer /aufnahme ohne Slug — verhindert dass die AppShell-Catchall
    // (path: '*') eine Anfrage ohne Slug stillschweigend zum /dashboard redirected.
    {
        path: '/aufnahme',
        element: (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
                <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-6 text-center shadow-sm">
                    <p className="text-lg font-semibold text-slate-900">Einrichtung nicht angegeben.</p>
                    <p className="text-sm text-slate-600 mt-2">
                        Der Aufruf-Link benötigt einen Einrichtungs-Slug
                        (z.B. <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700">/aufnahme/ihr-heim-name</code>).
                        Bitte prüfen Sie die Adresse oder kontaktieren Sie die Einrichtung direkt.
                    </p>
                </div>
            </div>
        ),
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    },
    // Public Family-Portal Universal-Link Bridge — kein Auth, kein AppShell.
    // Email/SMS-Magic-Link aus family-magic-link-issue Edge Fn landet hier
    // mit Token im Hash-Fragment. Page parst Hash, oeffnet Mobile-App via
    // Universal-Link/Custom-Scheme. Desktop: "Bitte am Smartphone oeffnen".
    {
        path: '/family/portal',
        lazy: familyPortalReceiverLazy,
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
    },
    {
        path: '/',
        element: (
            <AuthGuard>
                <AppShell />
            </AuthGuard>
        ),
        errorElement: <ErrorBoundary><div /></ErrorBoundary>,
        children: [
            { index: true, element: <Navigate to="/dashboard" replace /> },
            // Station (Kern — ungated)
            { path: 'dashboard', lazy: dashboardLazy },
            { path: 'location-overview', lazy: locationOverviewLazy },
            { path: 'handover', lazy: handoverLazy },
            // Bewohner (Kern — ungated)
            { path: 'residents', lazy: residentsLazy },
            { path: 'residents/:id', lazy: residentDetailLazy },
            { path: 'residents/:id/family-portal', lazy: residentFamilyPortalHubLazy },
            // Bewohner (gated)
            { path: 'sis', element: <CareFeatureRouteGuard featureKey="careSisDocumentation" />, children: [{ index: true, lazy: sisLazy }] },
            { path: 'sis/new', element: <CareFeatureRouteGuard featureKey="careSisDocumentation" />, children: [{ index: true, lazy: sisLazy }] },
            { path: 'wounds', element: <CareFeatureRouteGuard featureKey="careWoundMgmt" />, children: [
                { index: true, lazy: woundsLazy },
                { path: ':id', lazy: woundDetailLazy },
                { path: 'approval', lazy: woundApprovalLazy },
            ] },
            { path: 'medications', element: <CareFeatureRouteGuard featureKey="careMedications" />, children: [{ index: true, lazy: medicationsLazy }] },
            // Verwaltung (gated)
            { path: 'admissions', element: <CareFeatureRouteGuard featureKey="careAdmissions" />, children: [{ index: true, lazy: admissionsLazy }] },
            { path: 'admissions/new', element: <CareFeatureRouteGuard featureKey="careAdmissions" />, children: [{ index: true, lazy: admissionsLazy }] },
            { path: 'inquiries', element: <CareFeatureRouteGuard featureKey="careAdmissions" />, children: [{ index: true, lazy: inquiriesLazy }] },
            { path: 'occupancy', element: <CareFeatureRouteGuard featureKey="careOccupancy" />, children: [{ index: true, lazy: occupancyLazy }] },
            { path: 'inventory', element: <CareFeatureRouteGuard featureKey="careInventory" />, children: [{ index: true, lazy: inventoryLazy }] },
            // System (ungated)
            { path: 'settings', lazy: settingsLazy },
            // Admin (is_admin=true RLS-gated; UI-Gate in Route Component)
            { path: 'admin/glossary', lazy: adminGlossaryLazy },
            // Verwaltung (gated — Starter+)
            { path: 'schedule', element: <CareFeatureRouteGuard featureKey="careRoster" />, children: [{ index: true, lazy: scheduleLazy }] },
            // Kommunikation (gated — Starter+)
            { path: 'inbox', element: <CareFeatureRouteGuard featureKey="careInbox" />, children: [{ index: true, lazy: inboxLazy }] },
            { path: 'contacts', element: <CareFeatureRouteGuard featureKey="careContacts" />, children: [{ index: true, lazy: contactsLazy }] },
            { path: 'practice-requests', element: <CareFeatureRouteGuard featureKey="carePracticeRequests" />, children: [{ index: true, lazy: practiceRequestsLazy }] },
            { path: 'netzwerk', element: <CareFeatureRouteGuard featureKey="carePracticeRequests" />, children: [{ index: true, lazy: netzwerkLazy }] },
            // Unbekannte Pfade zeigen die 404-Seite statt still auf /dashboard
            // umzuleiten — der Redirect verschluckte Tippfehler und tote Links.
            // Bleibt innerhalb des AuthGuard: nicht angemeldete Aufrufe landen
            // weiterhin zuerst am Login, nicht auf der 404.
            { path: '*', element: <NotFound /> },
        ],
    },
]);
